import { Children, useEffect, useCallback, useRef, useState, type ReactNode } from "react";
import { ChevronLeft } from "lucide-react";

/* Panes narrower than these floors aren't usable; below their sum the split
   collapses to a single pane, deepest-first (detail wins over list). */
const DEFAULT_MIN_PRIMARY = 420;
const DEFAULT_MIN_SECONDARY = 360;

export function FocusSplit({
    children,
    secondaryOpen,
    secondaryActive,
    secondaryExpanded = false,
    onCollapseBack,
    backLabel = "Back",
    defaultPrimaryWidth = 620,
    minPrimaryWidth = DEFAULT_MIN_PRIMARY,
    minSecondaryWidth = DEFAULT_MIN_SECONDARY,
    storageKey,
    className = "",
}: {
    children: ReactNode;
    secondaryOpen: boolean;
    /** Which pane wins when only one fits. Defaults to the detail whenever it
        is open; routes that always render the detail (create/edit forms) pass
        their selection state here instead. */
    secondaryActive?: boolean;
    secondaryExpanded?: boolean;
    /** When set, a back bar renders above a collapsed detail so narrow
        screens can return to the list (usually clears the selection). */
    onCollapseBack?: () => void;
    backLabel?: string;
    defaultPrimaryWidth?: number;
    minPrimaryWidth?: number;
    minSecondaryWidth?: number;
    storageKey: string;
    className?: string;
}) {
    const rootRef = useRef<HTMLDivElement | null>(null);
    const observerRef = useRef<ResizeObserver | null>(null);
    const [width, setWidth] = useState(0);
    const [primaryWidth, setPrimaryWidth] = useState(() => Number(localStorage.getItem(storageKey)) || defaultPrimaryWidth);

    /* Callback ref with a synchronous first measure: an effect-based observer
       reports one frame late, so the first paint could show the wrong pane. */
    const setRoot = useCallback((el: HTMLDivElement | null) => {
        rootRef.current = el;
        observerRef.current?.disconnect();
        observerRef.current = null;
        if (!el) return;
        setWidth(el.clientWidth);
        const observer = new ResizeObserver(() => setWidth(el.clientWidth));
        observer.observe(el);
        observerRef.current = observer;
    }, []);

    /* Container-driven, not viewport-driven: the split adapts to the space it
       actually has (sidebar open, context rail, tablet split-screen, …). */
    const collapsed = width > 0 && width < minPrimaryWidth + minSecondaryWidth;
    const detailWins = secondaryOpen && (secondaryActive ?? true);
    const showBoth = secondaryOpen && !secondaryExpanded && !collapsed;
    const fittedPrimaryWidth = width > 0
        ? Math.min(Math.max(minPrimaryWidth, primaryWidth), Math.max(minPrimaryWidth, width - minSecondaryWidth))
        : primaryWidth;

    const finishResize = useRef<(() => void) | null>(null);
    useEffect(() => () => finishResize.current?.(), []);
    useEffect(() => { if (!showBoth) finishResize.current?.(); }, [showBoth]);

    function beginResize(event: React.PointerEvent<HTMLDivElement>) {
        if (event.button !== 0 || !event.isPrimary) return;
        event.preventDefault();
        finishResize.current?.();
        const root = rootRef.current;
        if (!root) return;
        const handle = event.currentTarget;
        const pointerId = event.pointerId;
        const startX = event.clientX;
        const startWidth = root.querySelector(".focus-split-primary")!.getBoundingClientRect().width;
        let latestWidth = startWidth;
        const move = (next: PointerEvent) => {
            if (next.pointerId !== pointerId) return;
            // Recover even if a browser/OS swallowed the release event.
            if (next.buttons === 0) { finish(); return; }
            const max = Math.max(minPrimaryWidth, root.clientWidth - minSecondaryWidth);
            latestWidth = Math.min(max, Math.max(minPrimaryWidth, startWidth + next.clientX - startX));
            setPrimaryWidth(latestWidth);
        };
        const end = (next: PointerEvent) => { if (next.pointerId === pointerId) finish(); };
        const finish = () => {
            window.removeEventListener("pointermove", move);
            window.removeEventListener("pointerup", end);
            window.removeEventListener("pointercancel", end);
            window.removeEventListener("blur", finish);
            handle.removeEventListener("lostpointercapture", end);
            finishResize.current = null;
            root.classList.remove("is-resizing");
            if (handle.hasPointerCapture(pointerId)) handle.releasePointerCapture(pointerId);
            localStorage.setItem(storageKey, String(Math.round(latestWidth)));
        };
        handle.setPointerCapture(pointerId);
        root.classList.add("is-resizing");
        finishResize.current = finish;
        window.addEventListener("pointermove", move);
        window.addEventListener("pointerup", end);
        window.addEventListener("pointercancel", end);
        window.addEventListener("blur", finish);
        handle.addEventListener("lostpointercapture", end);
    }

    const [primary, secondary] = Children.toArray(children);
    return (
        <div ref={setRoot} className={`focus-split${secondaryOpen ? " has-secondary" : ""}${secondaryExpanded ? " secondary-expanded" : ""}${collapsed ? " is-collapsed" : ""}${detailWins ? " secondary-active" : ""} ${className}`}>
            {collapsed && detailWins && onCollapseBack && (
                <button type="button" className="focus-split-back" onClick={onCollapseBack}>
                    <ChevronLeft size={16} /> {backLabel}
                </button>
            )}
            <div className="focus-split-primary" style={showBoth ? { width: fittedPrimaryWidth, flex: "0 0 auto" } : undefined}>{primary}</div>
            {showBoth && <div className="focus-split-resizer" role="separator" aria-label="Resize focus areas" aria-orientation="vertical" onPointerDown={beginResize} />}
            {secondaryOpen && <div className="focus-split-secondary">{secondary}</div>}
        </div>
    );
}
